// A production run's page (/runs/PR-001, ERP-PLAN §5.13; Mitch, 2026-11-04: a project is set up once, the work is on
// its runs): its header (Runs › the run, its project and CM, its stages and dates) and its tabs (?rt=units|orders|costs|
// landed|presales|timeline). Everything is worked out from synced rows through shared/costs.ts (useCostIndex,
// runResult), so tabs switch at once and numbers agree with the MCP to the cent. New run (NewRunDialog, from the Runs
// page) asks first whether it's one to plan or a past one to record (migration 057): a past run is closed from the
// start, marked Past run, and shows when it ran instead of the status steps.
import { Check, History, Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { runResult } from "../../../../../shared/costs.ts";
import { planToday } from "../../../../../shared/day.ts";
import type { ProductionRun } from "../../../../../shared/db.gen.ts";
import { PO_STATUS_LABELS, poDates } from "../../../../../shared/po.ts";
import { runPresales } from "../../../../../shared/presales.ts";
import { projectPath } from "../../../../../shared/projects.ts";
import { RUN_STATUS_HINTS, RUN_STATUS_LABELS, RUN_STATUSES, runLabel, runPath } from "../../../../../shared/runs.ts";
import { shortDate, useCostIndex } from "../../../lib/costs.ts";
import { qty } from "../../../lib/format.ts";
import { can, mutateWithResult } from "../../../lib/mutate.ts";
import { usePresaleIndex } from "../../../lib/presales.ts";
import { Link, navigate, setSearch, useSearch } from "../../../lib/router.tsx";
import { useRows } from "../../../lib/sync.ts";
import { Badge, Button, cx, Input } from "../../../ui/controls.tsx";
import { DialogLayer } from "../../../ui/DialogLayer.tsx";
import { PageHeader, Tabs } from "../../../ui/PageParts.tsx";
import { Fields } from "../../../ui/RecordPanel.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why } from "../../../ui/Why.tsx";
import type { TabProps } from "../Workspace.tsx";
import { RunCosts } from "./RunCosts.tsx";
import { LandedCostTab } from "./RunLanded.tsx";
import { OrdersTab } from "./RunPlan.tsx";
import { RunPresalesTab } from "./RunPresales.tsx";
import { RUN_TONE } from "./RunRows.tsx";
import { RunTimeline } from "./RunTimeline.tsx";
import { UnitsTab } from "./RunUnits.tsx";

// Fewer tabs (Mitch, 2026-11-04: "There's still so many options, statistics, and numbers that just don't make any sense
// to me"): Units (what it makes and what happened to them), Orders, Costs, Landed cost (what a unit cost, to set in
// Shopify), Pre-sales only when it has one, Timeline last. Old links' tabs land on what took them over.
const RUN_TABS = ["units", "orders", "costs", "landed", "presales", "timeline"] as const;
type RunTab = (typeof RUN_TABS)[number];
const RUN_TAB_LABELS: Record<RunTab, string> = {
  units: "Units",
  orders: "Orders",
  costs: "Costs",
  landed: "Landed cost",
  presales: "Pre-sales", // Pre-sales --- reserved for backers, held, shipped, income and the result
  timeline: "Timeline",
};
const OLD_TABS: Record<string, RunTab> = { plan: "units", output: "units", result: "landed" };

export type RunProps = TabProps & { run: ProductionRun };

/**
 * New run: one to plan and build, or a past one to record (migration 057; Mitch: "it can be marked as a historical run
 * for product that was already completed"). Both start with a line per variant, units set on the Plan tab.
 */
export function NewRunDialog({ project, variants, onClose }: { project: TabProps["project"]; variants: string[]; onClose: () => void }) {
  const [past, setPast] = useState<boolean | null>(null);
  const [name, setName] = useState("");
  const [started, setStarted] = useState("");
  const [finished, setFinished] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && document.querySelectorAll("[data-dialog]").length === 1) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const today = planToday();
  const problem =
    past === null
      ? "Say whether it's done already"
      : past && !finished
        ? "Say when it finished"
        : past && finished > today
          ? "A past run finished on or before today"
          : past && started && started > finished
            ? "It can't finish before it started"
            : "";
  const create = async () => {
    if (problem) return;
    setBusy(true);
    const { ok, result } = await mutateWithResult("production_run.create", {
      id: crypto.randomUUID(),
      project_id: project.id,
      ...(name.trim() ? { name: name.trim() } : {}),
      ...(past ? { historical: true, started_on: started || null, finished_on: finished } : {}),
      lines: variants.map((id) => ({ item_id: id, qty: 0 })),
    });
    setBusy(false);
    const number = (result as { number?: string } | undefined)?.number;
    if (!ok || !number) return;
    toast.ok(past ? `${number} recorded as a past run: set the units it built` : `${number} started: set its units per variant`);
    onClose();
    navigate(runPath({ number }));
  };
  const choice = (value: boolean, title: string, text: string) => (
    <button
      type="button"
      onClick={() => setPast(value)}
      className={cx(
        "flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left",
        past === value ? "border-accent bg-accent-soft" : "border-line hover:border-line-strong hover:bg-hover",
      )}
    >
      <span className="font-medium">{title}</span>
      <span className="text-[12px] text-muted">{text}</span>
    </button>
  );
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div data-dialog className="w-[460px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        <div className="mb-3 text-[15px] font-semibold">New run of {project.name}</div>
        <div className="mb-1.5 text-[12px] text-muted">Is this batch done already?</div>
        <div className="flex flex-col gap-2">
          {choice(false, "No: plan a run", "A batch still to order and build. You set its units, order its parts and follow it through to delivery.")}
          {choice(
            true,
            "Yes: record a past run",
            "A batch that was already built and delivered. You record its units, when it ran, and its POs and invoices. Nothing moves stock or goes to Shopify or vendors, and planning leaves it out.",
          )}
        </div>
        <label className="mt-3 block text-[12px] text-muted">
          Name <span className="text-faint">(optional)</span>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={past ? "e.g. First batch (2025)" : "e.g. Holiday restock"}
            className="mt-0.5"
          />
        </label>
        {past && (
          <div className="mt-2 grid grid-cols-2 gap-2">
            <label className="block text-[12px] text-muted">
              Started <span className="text-faint">(optional)</span>
              <Input type="date" value={started} max={finished || today} onChange={(e) => setStarted(e.target.value)} className="mt-0.5" />
            </label>
            <label className="block text-[12px] text-muted">
              Finished
              <Input type="date" value={finished} max={today} onChange={(e) => setFinished(e.target.value)} className="mt-0.5" />
            </label>
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2">
          {problem && past !== null && <span className="mr-auto text-[12px] text-warn">{problem}</span>}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!!problem || busy} onClick={create} icon={past ? <History className="size-3.5" /> : undefined}>
            {past ? "Record the past run" : "Plan the run"}
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}

/** The run's page: its header, its tabs, and the tab picked in a scroll box of its own. */
export function RunWorkspace(props: RunProps) {
  const { run } = props;
  const params = useSearch();
  const asked = params.get("rt") ?? "";
  const rt: RunTab = (RUN_TABS as readonly string[]).includes(asked) ? (asked as RunTab) : (OLD_TABS[asked] ?? "units");
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const posCount = (cx_.posByRun.get(run.id) ?? []).length;
  const rp = runPresales(usePresaleIndex(), run.id);
  // Pre-sales only when the run has one (or held units); the Units tab says how to set one up
  const tabs = RUN_TABS.filter((t) => t !== "presales" || rp.presales.length > 0 || rp.holds.length > 0 || rt === "presales");
  const counts: Partial<Record<RunTab, string>> = {
    orders: posCount ? String(posCount) : "",
    costs: res ? String(res.rows.length) : "",
    presales: rp.totals.reserved ? qty(rp.totals.reserved) : rp.totals.held ? `${qty(rp.totals.held)} held` : "",
  };
  return (
    <div className="flex h-full min-h-0 flex-col">
      <RunHeader {...props} />
      <Tabs
        level="record"
        active={rt}
        onPick={(t) => setSearch({ rt: t === "units" ? null : t })}
        tabs={tabs.map((t) => ({
          key: t,
          label: RUN_TAB_LABELS[t],
          count: counts[t],
          dot:
            t === "presales" && rp.flags.length > 0
              ? { tone: "danger" as const, title: rp.flags.join("\n") }
              : t === "costs" && res && res.attention.length > 0
                ? { tone: "warn" as const, title: `Needs attention:\n${res.attention.map((a) => `${a.what}: needs ${a.needs.join(", ")}`).join("\n")}` }
                : undefined,
        }))}
      />
      <div className="min-h-0 flex-1 overflow-y-auto bg-bg">
        <div className="flex w-full flex-col gap-5 p-4">
          {rt === "units" && <UnitsTab {...props} />}
          {rt === "orders" && <OrdersTab {...props} />}
          {rt === "costs" && <RunCosts {...props} />}
          {rt === "landed" && <LandedCostTab {...props} />}
          {rt === "presales" && <RunPresalesTab {...props} />}
          {rt === "timeline" && <RunTimeline {...props} />}
        </div>
      </div>
    </div>
  );
}

/** Pre-sales --- "924 of 1,701 GCU130x pre-sold" per product the run makes, opening the Pre-sales tab. */
function PresaleChips({ run }: { run: ProductionRun }) {
  const rp = runPresales(usePresaleIndex(), run.id);
  if (!rp.chips.length) return null;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {rp.chips.map((c) => (
        <Why key={c.key} title={c.text} note={c.why} width={420}>
          <button
            type="button"
            onClick={() => setSearch({ rt: "presales" })}
            className="inline-flex h-5 items-center rounded bg-accent-soft px-1.5 text-[11px] font-medium whitespace-nowrap text-accent-text hover:brightness-95"
          >
            {c.text}
          </button>
        </Why>
      ))}
    </span>
  );
}

function RunHeader({ run, px, project }: RunProps) {
  const cm = run.cm_vendor_id ? px.vendorById.get(run.cm_vendor_id) : undefined;
  const [basics, setBasics] = useState(false);
  // The stage follows its make orders (runStageOf(), Mitch, 2026-11-04: "follow the order"): sending one moves it to
  // Ordering (and fixes the plan's prices), the CM's units, Shipped and receiving move it on. A person only closes a
  // received run, its costs final, or reopens a closed one.
  const makes = useRows("po").filter((p) => p.production_run_id === run.id && p.kind === "make" && p.status !== "cancelled");
  const follows = makes.length
    ? `Follows ${makes.map((p) => `${p.number} (${PO_STATUS_LABELS[p.status]})`).join(", ")}`
    : "No make order yet: it's in Planning until one goes to the CM";
  const close = async () => {
    if (!confirm(`Close ${run.number}? Its costs are final then: its actual cost counts as its products' landed cost.`)) return;
    await mutateWithResult("production_run.set_status", { id: run.id, status: "closed" });
  };
  const reopen = () => mutateWithResult("production_run.set_status", { id: run.id, status: "received" });
  const editable = can("production_run.set_status");
  const basis = (run.plan_basis as { at?: string })?.at;
  return (
    <>
      <PageHeader
        crumbs={[{ label: "Runs", to: "/runs" }]}
        title={<span title={runLabel(run)}>{runLabel(run)}</span>}
        actions={
          <>
            {can("production_run.update") && (
              <Button size="sm" variant="ghost" icon={<Pencil className="size-3.5" />} onClick={() => setBasics(true)}>
                Basics
              </Button>
            )}
            {editable && !run.historical && run.status === "closed" && (
              <Button size="sm" variant="ghost" onClick={() => void reopen()} title="Open it again: it takes the stage its make orders are at">
                Reopen
              </Button>
            )}
            {editable && !run.historical && run.status === "received" && (
              <Button size="sm" variant="primary" icon={<Check className="size-3.5" />} onClick={() => void close()} title={RUN_STATUS_HINTS.closed}>
                Close run
              </Button>
            )}
          </>
        }
      >
        {run.historical ? (
          <Why
            title="A past run"
            note="Recorded after it was done: its units built are on the Units tab, and nothing it records moves stock or goes to Shopify, vendors or planning."
          >
            <Badge tone="neutral">Past run</Badge>
          </Why>
        ) : (
          <Badge tone={RUN_TONE[run.status]}>{RUN_STATUS_LABELS[run.status]}</Badge>
        )}
        <span className="whitespace-nowrap">
          <span className="text-faint">Project </span>
          <Link to={projectPath(project)} className="hover:underline">
            {project.name}
          </Link>
        </span>
        <span className="whitespace-nowrap">
          <span className="text-faint">CM </span>
          {cm ? cm.short_name || cm.name : <span className="text-faint">not chosen</span>}
        </span>
      </PageHeader>
      {basics && <RunBasicsDialog run={run} onClose={() => setBasics(false)} />}
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line bg-surface px-4 py-2">
        {run.historical ? (
          <PastRunDates run={run} />
        ) : (
          <ol className="flex items-center gap-1 text-[11px]">
            {RUN_STATUSES.map((s, i) => {
              const at = RUN_STATUSES.indexOf(run.status);
              return (
                <li key={s} className="flex items-center gap-1" title={RUN_STATUS_HINTS[s]}>
                  <span
                    className={cx(
                      "flex h-5 items-center rounded-full px-2",
                      i < at ? "bg-ok-soft text-ok" : i === at ? "bg-accent text-white" : "bg-surface-2 text-faint",
                    )}
                  >
                    {i < at ? "✓ " : ""}
                    {RUN_STATUS_LABELS[s]}
                  </span>
                  {i < RUN_STATUSES.length - 1 && <span className={cx("h-px w-4", i < at ? "bg-ok" : "bg-line-strong")} />}
                </li>
              );
            })}
          </ol>
        )}
        {!run.historical && run.status !== "closed" && <span className="text-[12px] text-muted">{follows}</span>}
        {!run.historical && <RunDates run={run} />}
        <PresaleChips run={run} />
        {basis && !run.historical && (
          <Why
            title="The plan is fixed"
            note={`Its estimates use the quotes and rates of ${shortDate(basis)}, until units are built. Use today's prices on the Landed cost tab.`}
          >
            <span className="text-[12px] text-muted">prices fixed {shortDate(basis)}</span>
          </Why>
        )}
      </div>
    </>
  );
}

/**
 * The run's make PO: when it was ordered and when its units arrived (shared/po.ts poDates()), else the run's own dates.
 * A past run's are typed in (PastRunDates). With both, how long it took and, once, that as the CM's lead time (Mitch,
 * 2026-11-04: "the date we sent an order, when the units were received, that's the stuff which is important to be able
 * to extrapolate the lead times").
 */
function useRunDates(run: ProductionRun) {
  const pos = useRows("po");
  const receipts = useRows("receipt");
  const makes = pos.filter((p) => p.production_run_id === run.id && p.kind === "make" && p.status !== "cancelled");
  // Several make POs: ordered with the first, received with the last once all are in
  const dates = run.historical
    ? []
    : makes.map((m) =>
        poDates(
          m,
          receipts.filter((r) => r.po_id === m.id),
        ),
      );
  const first = dates
    .map((d) => d.ordered)
    .filter((d): d is string => !!d)
    .sort()[0];
  const last =
    dates.length && dates.every((d) => d.received)
      ? (dates
          .map((d) => d.received as string)
          .sort()
          .at(-1) ?? null)
      : null;
  const ordered = first ?? run.started_on ?? null;
  const received = last ?? run.finished_on ?? null;
  const days = ordered && received ? Math.round((Date.parse(received) - Date.parse(ordered)) / 86_400_000) : null;
  // The CM's lead time from it: with one make PO (several are for the CM's builds in pieces)
  return { make: makes.length === 1 ? makes[0] : undefined, ordered, received, days: days != null && days >= 0 ? days : null };
}

function RunDates({ run }: { run: ProductionRun }) {
  const { make, ordered, received, days } = useRunDates(run);
  if (!ordered) return null;
  return (
    <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
      <span>
        <span className="text-faint">Ordered </span>
        {shortDate(ordered)}
      </span>
      {received && (
        <span>
          <span className="text-faint">Received </span>
          {shortDate(received)}
        </span>
      )}
      <TookDays make={make} days={days} />
    </span>
  );
}

/** "Took 28 days", and making that the CM's lead time for what its make PO builds (po.use_lead_time). */
function TookDays({ make, days }: { make: ReturnType<typeof useRunDates>["make"]; days: number | null }) {
  const vendor = useRows("vendor").find((v) => v.id === make?.vendor_id);
  const lines = useRows("po_line").filter((l) => l.po_id === make?.id);
  const links = useRows("vendor_item").filter((l) => l.vendor_id === make?.vendor_id && lines.some((x) => x.item_id === l.item_id));
  if (days == null) return null;
  const name = vendor ? vendor.short_name || vendor.name : "the CM";
  const same = lines.length > 0 && lines.every((l) => links.find((k) => k.item_id === l.item_id)?.lead_time_days === days);
  return (
    <span className="flex items-center gap-2">
      <span>
        <span className="text-faint">Took </span>
        <span className="font-medium">{days} days</span>
      </span>
      {make &&
        lines.length > 0 &&
        (same ? (
          <span className="text-faint">{name}'s lead time</span>
        ) : (
          can("po.use_lead_time") && (
            <Button
              size="sm"
              variant="ghost"
              title={`Set ${name}'s lead time for what this run builds to ${days} days, so planning starts builds that far ahead`}
              onClick={async () => {
                const { ok } = await mutateWithResult("po.use_lead_time", { po_id: make.id, days });
                if (ok) toast.ok(`${name}'s lead time is ${days} days for what this run builds`);
              }}
            >
              Use as {name}'s lead time
            </Button>
          )
        ))}
    </span>
  );
}

/** A past run: when it was ordered and when its units arrived (changed here any time), and how long that took. */
function PastRunDates({ run }: { run: ProductionRun }) {
  const editable = can("production_run.update");
  const { make, days } = useRunDates(run);
  const save = (patch: { started_on?: string | null; finished_on?: string | null }) => mutateWithResult("production_run.update", { id: run.id, patch });
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
      <span className="flex items-center gap-1.5">
        <span className="whitespace-nowrap text-muted">Ordered on</span>
        <Input
          type="date"
          disabled={!editable}
          value={run.started_on ?? ""}
          max={run.finished_on ?? undefined}
          onChange={(e) => save({ started_on: e.target.value || null })}
          className="h-7 w-36"
        />
      </span>
      <span className="flex items-center gap-1.5">
        <span className="whitespace-nowrap text-muted">Received on</span>
        <Input
          type="date"
          disabled={!editable}
          value={run.finished_on ?? ""}
          max={planToday()}
          onChange={(e) => e.target.value && save({ finished_on: e.target.value })}
          className="h-7 w-36"
        />
      </span>
      <TookDays make={make} days={days} />
    </div>
  );
}

/** A run's basics in a dialog beside its header's button (as a project's are): each change saves as it's made. */
function RunBasicsDialog({ run, onClose }: { run: ProductionRun; onClose: () => void }) {
  const vendors = useRows("vendor");
  const past = run.historical;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && document.querySelectorAll("[data-dialog]").length === 1) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[480px] rounded-xl border border-line bg-surface p-3 shadow-pop">
        <div className="mb-1 px-2 text-[15px] font-semibold">{run.number} basics</div>
        <div className="mb-2 px-2 text-[12px] text-muted">Each change saves as you make it.</div>
        <div className="flex flex-col gap-0.5">
          <Fields
            row={run}
            update="production_run.update"
            disabled={!can("production_run.update") || (run.status === "closed" && !past)}
            fields={[
              { key: "name", label: "Name", placeholder: past ? "e.g. First batch (2025)" : "e.g. Holiday restock" },
              {
                key: "cm_vendor_id",
                label: "CM",
                type: "select",
                nullable: true,
                options: [
                  { value: "", label: "Not chosen" },
                  ...vendors
                    .filter((v) => (!v.archived && v.kind !== "supplier") || v.id === run.cm_vendor_id)
                    .map((v) => ({ value: v.id, label: v.short_name || v.name })),
                ],
              },
              ...(past
                ? []
                : ([
                    { key: "target_ship", label: "Target ship", type: "date" },
                    { key: "target_arrive", label: "Target arrival", type: "date" },
                  ] as const)),
              { key: "notes", label: "Notes", type: "textarea" },
            ]}
          />
        </div>
        <div className="mt-3 flex justify-end px-2">
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </DialogLayer>
  );
}
